import { useState, useEffect } from "react";
import { CreditCard, Banknote, Smartphone, UtensilsCrossed, Receipt, WifiOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePaymentMethods, PaymentMethod, PaymentMethodType } from "@/hooks/usePaymentMethods";
import { useServiceTax } from "@/hooks/useServiceTax";
import { printCustomerReceipt } from "@/lib/order-printing";
import { useSQLiteLocal } from "@/hooks/useSQLiteLocal";
import { useSQLiteSync } from "@/hooks/useSQLiteSync";
import type { OpenConsumption } from "@/hooks/useOpenConsumption";

interface CloseConsumptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consumption: OpenConsumption | null;
  onSuccess: () => void;
}

const paymentTypeIcons: Record<PaymentMethodType, React.ReactNode> = {
  cash: <Banknote className="h-4 w-4" />,
  card: <CreditCard className="h-4 w-4" />,
  digital_wallet: <Smartphone className="h-4 w-4" />,
};

export function CloseConsumptionDialog({
  open,
  onOpenChange,
  consumption,
  onSuccess,
}: CloseConsumptionDialogProps) {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [amountReceived, setAmountReceived] = useState("");
  const [applyServiceTax, setApplyServiceTax] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  const { getActivePaymentMethods } = usePaymentMethods();
  const { serviceTaxPercentage, serviceTaxEnabled, calculateServiceTax } = useServiceTax();
  const { isReady: sqliteReady, addOrderPayment, closeOrderOffline, getPaymentMethods: getLocalPaymentMethods } = useSQLiteLocal();
  const { isOnline } = useSQLiteSync();
  const activePaymentMethods = getActivePaymentMethods();

  useEffect(() => {
    if (open) {
      // Use local payment methods if offline
      if (!isOnline && sqliteReady) {
        const localMethods = getLocalPaymentMethods();
        if (localMethods.length > 0) {
          const first = localMethods[0];
          setSelectedPaymentMethod({
            id: first.id,
            name: first.name,
            type: (first.type || 'cash') as PaymentMethodType,
            fee_percentage: first.fee_percentage,
            is_active: first.is_active === 1,
            restaurant_id: first.restaurant_id,
            created_at: '',
            updated_at: '',
            receiving_days: 0,
            is_system_default: false,
            chart_account_id: null,
            fiscal_code: null,
            icon_color: null,
          });
        }
        setIsOfflineMode(true);
      } else {
        setIsOfflineMode(false);
        if (activePaymentMethods.length > 0) {
          setSelectedPaymentMethod(activePaymentMethods[0]);
        }
      }
      setAmountReceived("");
      setApplyServiceTax(serviceTaxEnabled);
    }
  }, [open, activePaymentMethods, serviceTaxEnabled, isOnline, sqliteReady, getLocalPaymentMethods]);

  if (!consumption) return null;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const subtotal = consumption.subtotal;
  const serviceTaxAmount = applyServiceTax && serviceTaxEnabled ? calculateServiceTax(subtotal) : 0;
  const total = subtotal + serviceTaxAmount;

  const isCashPayment = selectedPaymentMethod?.type === "cash";
  const amountReceivedValue = parseFloat(amountReceived) || 0;
  const changeAmount = isCashPayment && amountReceivedValue > total ? amountReceivedValue - total : 0;

  const handleClose = async () => {
    if (!selectedPaymentMethod) return;

    setIsSubmitting(true);
    try {
      const feeAmount = selectedPaymentMethod.type === "card" && selectedPaymentMethod.fee_percentage > 0
        ? (total * selectedPaymentMethod.fee_percentage) / 100
        : 0;

      // Note: OpenConsumption doesn't have tableId/commandId, we pass null 
      // and the sync queue will handle the update by order ID
      const tableId: string | null = null;
      const commandId: string | null = null;

      if (isOnline) {
        // Online mode - use Supabase directly
        // Update all orders status to delivered
        const { error: orderError } = await supabase
          .from("orders")
          .update({
            status: "delivered",
            payment_method_id: selectedPaymentMethod.id,
          })
          .in("id", consumption.orderIds);

        if (orderError) throw orderError;

        // Create payment for the first order (with full amount)
        const { error: paymentError } = await supabase
          .from("order_payments")
          .insert({
            order_id: consumption.orderIds[0],
            payment_method_id: selectedPaymentMethod.id,
            amount: total,
            fee_amount: feeAmount,
          });

        if (paymentError) throw paymentError;

        // Update table/command status - use number directly to handle orphan orders
        // where current_order_id may already be null
        if (consumption.type === "table") {
          const { data: restaurantData } = await supabase
            .from("orders")
            .select("restaurant_id")
            .eq("id", consumption.orderIds[0])
            .single();
          
          if (restaurantData) {
            await supabase
              .from("tables")
              .update({ status: "available", current_order_id: null })
              .eq("restaurant_id", restaurantData.restaurant_id)
              .eq("number", consumption.number);
          }
        } else {
          const { data: restaurantData } = await supabase
            .from("orders")
            .select("restaurant_id")
            .eq("id", consumption.orderIds[0])
            .single();
          
          if (restaurantData) {
            await supabase
              .from("commands")
              .update({ status: "available", current_order_id: null, customer_name: null })
              .eq("restaurant_id", restaurantData.restaurant_id)
              .eq("number", consumption.number);
          }
        }
      } else if (sqliteReady) {
        // Offline mode - use SQLite local
        console.log('[CloseConsumption] Processing offline closure...');
        
        // Process each order
        for (const orderId of consumption.orderIds) {
          // Add payment
          addOrderPayment({
            id: crypto.randomUUID(),
            order_id: orderId,
            payment_method_id: selectedPaymentMethod.id,
            payment_method_name: selectedPaymentMethod.name,
            amount: total / consumption.orderIds.length, // Split evenly if multiple orders
            fee_amount: feeAmount / consumption.orderIds.length,
            created_at: new Date().toISOString(),
          });

          // Close order
          closeOrderOffline(
            orderId,
            'delivered',
            serviceTaxAmount,
            total,
            tableId,
            commandId,
            0, // discount
            0, // couvertQuantity
            0, // couvertAmount
            selectedPaymentMethod.id, // paymentMethodId
            feeAmount, // paymentFee
            1 // peopleCount
          );
        }

        toast.info("Fechamento salvo localmente", {
          description: "Será sincronizado quando a conexão voltar",
        });
      } else {
        throw new Error("SQLite não está pronto e não há conexão");
      }

      // Print customer receipt (best effort)
      try {
        await printCustomerReceipt({
          orderNumber: 0,
          items: consumption.items.map(i => ({
            name: i.product_name,
            quantity: i.quantity,
            unit_price: i.unit_price,
            total_price: i.total_price,
          })),
          subtotal,
          serviceTax: serviceTaxAmount,
          discount: 0,
          total,
          paymentMethod: selectedPaymentMethod.name,
          amountReceived: isCashPayment ? amountReceivedValue : undefined,
          change: changeAmount > 0 ? changeAmount : undefined,
          customerName: consumption.customerName || undefined,
        });
      } catch (printError) {
        console.error('[CloseConsumption] Print error:', printError);
      }

      toast.success("Consumo fechado com sucesso!");
      onSuccess();
    } catch (error) {
      console.error("Error closing consumption:", error);
      toast.error("Erro ao fechar consumo");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {consumption.type === "table" ? (
              <UtensilsCrossed className="h-5 w-5 text-primary" />
            ) : (
              <Receipt className="h-5 w-5 text-primary" />
            )}
            <DialogTitle>
              Fechar {consumption.type === "table" ? "Mesa" : "Comanda"} {consumption.number}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Summary */}
          <div className="rounded-lg bg-muted p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Itens</span>
              <span>{consumption.items.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            {serviceTaxEnabled && (
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={applyServiceTax}
                    onCheckedChange={setApplyServiceTax}
                    className="scale-75"
                  />
                  <span className="text-muted-foreground">Taxa de Serviço ({serviceTaxPercentage}%)</span>
                </div>
                <span className={applyServiceTax ? "" : "text-muted-foreground line-through"}>
                  +{formatCurrency(serviceTaxAmount)}
                </span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="space-y-2">
            <Label>Forma de Pagamento</Label>
            <div className="grid grid-cols-2 gap-2">
              {activePaymentMethods.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  onClick={() => setSelectedPaymentMethod(method)}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-left",
                    selectedPaymentMethod?.id === method.id
                      ? "border-primary bg-primary/5"
                      : "border-muted hover:border-primary/50"
                  )}
                >
                  {paymentTypeIcons[method.type]}
                  <span className="text-sm font-medium truncate">{method.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Cash Payment - Amount Received */}
          {isCashPayment && (
            <div className="space-y-2">
              <Label htmlFor="amountReceived">Valor Recebido</Label>
              <Input
                id="amountReceived"
                type="number"
                min="0"
                step="0.01"
                placeholder="R$ 0,00"
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
              />
              {changeAmount > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">Troco</span>
                  <span className="text-lg font-bold text-green-700 dark:text-green-400">
                    {formatCurrency(changeAmount)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleClose} disabled={!selectedPaymentMethod || isSubmitting}>
            {isSubmitting ? "Fechando..." : "Fechar Conta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
